<template>
  <div class="account-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>账户中心</h2>
      <el-button type="default" icon="el-icon-refresh" @click="handleRefresh" :loading="loading.refresh">
        刷新
      </el-button>
    </div>

    <!-- 积分卡片 -->
    <el-card class="balance-card">
      <div class="balance-header">
        <div class="balance-info">
          <div class="balance-label">账户积分</div>
          <div class="balance-amount">{{ formatPoints(points) }}</div>
          <div class="balance-sub">≈ ¥{{ pointsToYuan(points) }}</div>
        </div>
        <div class="balance-actions">
          <el-button type="primary" size="large" @click="handleRecharge">
            <i class="el-icon-plus"></i> 立即充值
          </el-button>

        </div>
      </div>
      <div class="balance-stats">
        <div class="stat-item">
          <div class="stat-value">1积分</div>
          <div class="stat-label">¥1</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">0.8积分/条</div>
          <div class="stat-label">线索</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">0.08积分/条</div>
          <div class="stat-label">发送私信</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ formatPoints(totalPoints) }}</div>
          <div class="stat-label">累计获得积分</div>
        </div>
      </div>
    </el-card>

    <!-- 标签页：明细 & 按天 & 按月 -->
    <el-card class="records-card">
      <el-tabs v-model="activeTab" @tab-click="handleTabChange">
        <!-- 明细 -->
        <el-tab-pane label="明细" name="detail">
          <!-- 筛选表单 -->
          <div class="filter-form">
            <el-select v-model="detailFilter.type" placeholder="类型" clearable size="small"
              style="width: 120px; margin-right: 10px;">
              <el-option label="全部" value="" />
              <el-option label="收入" value="income" />
              <el-option label="支出" value="expense" />
            </el-select>
            <el-date-picker v-model="detailFilter.dateRange" type="daterange" range-separator="至"
              start-placeholder="开始日期" end-placeholder="结束日期" size="small" style="margin-right: 10px;"
              value-format="timestamp" />
            <el-button type="primary" size="small" @click="handleDetailFilter">查询</el-button>
            <el-button size="small" @click="resetDetailFilter">重置</el-button>
          </div>
          <el-table :data="detailRecords" v-loading="loading.detail" stripe height="260">
            <el-table-column prop="id" label="ID" width="80" />
            <el-table-column label="类型" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.direction === 'income' ? 'success' : 'danger'" size="small">
                  {{ scope.row.direction === 'income' ? '收入' : '支出' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="变动金额" width="120">
              <template slot-scope="scope">
                <span :class="scope.row.direction === 'income' ? 'amount-positive' : 'amount-negative'">
                  {{ scope.row.direction === 'income' ? '+' : '-' }}{{ formatPoints(scope.row.amount) }} 积分
                </span>
              </template>
            </el-table-column>

            <el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip />
            <el-table-column prop="createTime" label="时间" width="170">
              <template slot-scope="scope">
                {{ formatTime(scope.row.createTime * 1000) }}
              </template>
            </el-table-column>
          </el-table>
          <div class="pagination-wrapper" v-show="true">
            <el-pagination @current-change="handleDetailPageChange" :current-page.sync="detailPagination.page"
              :page-size="detailPagination.pageSize" layout="total, prev, pager, next"
              :total="detailPagination.total || 0" :hide-on-single-page="false" />
          </div>
        </el-tab-pane>

        <!-- 按天 -->
        <el-tab-pane label="按天" name="day">
          <el-table :data="dayRecords" v-loading="loading.day" stripe>
            <el-table-column prop="date" label="日期" />
            <el-table-column prop="count" label="笔数" />
            <el-table-column label="收入">
              <template slot-scope="scope">
                <span class="amount-positive">+{{ formatPoints(scope.row.income) }} 积分</span>
              </template>
            </el-table-column>
            <el-table-column label="支出">
              <template slot-scope="scope">
                <span class="amount-negative">-{{ formatPoints(scope.row.expense) }} 积分</span>
              </template>
            </el-table-column>
            <el-table-column label="净变动">
              <template slot-scope="scope">
                <span :class="scope.row.net_change >= 0 ? 'amount-positive' : 'amount-negative'">
                  {{ scope.row.net_change >= 0 ? '+' : '' }}{{ formatPoints(scope.row.net_change) }} 积分
                </span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- 按月 -->
        <el-tab-pane label="按月" name="month">
          <el-table :data="monthRecords" v-loading="loading.month" stripe>
            <el-table-column prop="month" label="月份" />
            <el-table-column prop="count" label="笔数" />
            <el-table-column label="收入">
              <template slot-scope="scope">
                <span class="amount-positive">+{{ formatPoints(scope.row.income) }} 积分</span>
              </template>
            </el-table-column>
            <el-table-column label="支出">
              <template slot-scope="scope">
                <span class="amount-negative">-{{ formatPoints(scope.row.expense) }} 积分</span>
              </template>
            </el-table-column>
            <el-table-column label="净变动">
              <template slot-scope="scope">
                <span :class="scope.row.net_change >= 0 ? 'amount-positive' : 'amount-negative'">
                  {{ scope.row.net_change >= 0 ? '+' : '' }}{{ formatPoints(scope.row.net_change) }} 积分
                </span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <!-- 充值弹窗 -->
    <recharge-modal ref="rechargeModal" @recharged="handleRecharged" />
  </div>
</template>

<script>
import Decimal from 'decimal.js'
import RechargeModal from './RechargeModal'

export default {
  name: 'AccountPage',
  components: { RechargeModal },

  data() {
    return {
      // 定价策略
      orgPriceDetail: {},
      // 积分信息
      points: 0,           // 可用积分
      totalPoints: 0,      // 累计获得积分
      totalRecharge: 0,
      totalCost: 0,

      // 当前标签页
      activeTab: 'detail',

      // 加载状态
      loading: {
        detail: false,
        day: false,
        month: false,
        refresh: false,      // 刷新按钮加载状态
      },

      // 明细记录
      detailRecords: [],
      detailPagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      // 明细筛选条件
      detailFilter: {
        type: '',
        dateRange: null
      },

      // 按天统计
      dayRecords: [],
      dayPagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 按月统计
      monthRecords: [],
      monthPagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },

  mounted() {
    this.fetchBalance()
    this.fetchDetailRecords()
    this.fetchDayRecords()
    this.fetchMonthRecords()
  },

  computed: {
    // 获取组织ID（充值和积分按组织维度）
    organizeId() {
      return this.$store.state.user_login_info?.organize_id
    }
  },

  methods: {
    // 获取积分信息
    async fetchBalance() {
      if (!this.organizeId) return
      try {
        const { data } = await this.$http.get(`/RechargeTransaction/balance/${this.organizeId}`)
        if (data.meta?.status === 200) {
          this.points = data.data.remainScore || data.data.points || 0
          this.totalPoints = data.data.total_points || 0
        }
      } catch (error) {
        console.error('获取积分余额失败:', error)
      }
    },



    // 获取明细记录
    async fetchDetailRecords() {
      if (!this.organizeId) return
      this.loading.detail = true
      try {
        const params = {
          org_id: this.organizeId,
          pagenum: this.detailPagination.page,
          pagesize: this.detailPagination.pageSize
        }
        // 添加类型筛选
        if (this.detailFilter.type) {
          params.type = this.detailFilter.type
        }
        // 添加时间范围筛选
        if (this.detailFilter.dateRange && this.detailFilter.dateRange.length === 2) {
          params.startTime = Math.floor(this.detailFilter.dateRange[0] / 1000)
          params.endTime = Math.floor(this.detailFilter.dateRange[1] / 1000)
        }
        const { data } = await this.$http.get('/RechargeTransaction/records', { params })
        if (data.status === 200 || data.meta?.status === 200) {
          this.detailRecords = data.data.records || []
          this.detailRecords = this.detailRecords.concat(this.detailRecords)
          this.detailPagination.total = data.data.total || 0
        }
      } catch (error) {
        console.error('获取明细记录失败:', error)
      } finally {
        this.loading.detail = false
      }
    },

    // 获取按天统计
    async fetchDayRecords() {
      if (!this.organizeId) return
      this.loading.day = true
      try {
        const params = {
          org_id: this.organizeId,
          pagenum: this.dayPagination.page,
          pagesize: this.dayPagination.pageSize
        }
        const { data } = await this.$http.get('/RechargeTransaction/records/by-day', { params })
        if (data.status === 200 || data.meta?.status === 200) {
          this.dayRecords = data.data.records || []
          this.dayPagination.total = data.data.total || 0
        }
      } catch (error) {
        console.error('获取按天统计失败:', error)
      } finally {
        this.loading.day = false
      }
    },

    // 获取按月统计
    async fetchMonthRecords() {
      if (!this.organizeId) return
      this.loading.month = true
      try {
        const params = {
          org_id: this.organizeId,
          pagenum: this.monthPagination.page,
          pagesize: this.monthPagination.pageSize
        }
        const { data } = await this.$http.get('/RechargeTransaction/records/by-month', { params })
        if (data.status === 200 || data.meta?.status === 200) {
          this.monthRecords = data.data.records || []
          this.monthPagination.total = data.data.total || 0
        }
      } catch (error) {
        console.error('获取按月统计失败:', error)
      } finally {
        this.loading.month = false
      }
    },

    // 打开充值弹窗
    handleRecharge() {
      this.$refs.rechargeModal.show()
    },

    // 充值成功回调
    handleRecharged() {
      this.fetchBalance()
      this.fetchDetailRecords()
    },

    // 刷新按钮点击事件
    async handleRefresh() {
      this.loading.refresh = true
      try {
        await Promise.all([
          this.fetchBalance(),
          this.fetchDetailRecords(),
          this.fetchDayRecords(),
          this.fetchMonthRecords()
        ])
        this.$message.success('刷新成功')
      } catch (error) {
        this.$message.error('刷新失败')
      } finally {
        this.loading.refresh = false
      }
    },

    // 标签页切换
    handleTabChange() {
      // 切换标签页时可以做一些刷新操作
    },

    // 明细分页
    handleDetailPageChange(page) {
      this.detailPagination.page = page
      this.fetchDetailRecords()
    },

    // 明细筛选查询
    handleDetailFilter() {
      this.detailPagination.page = 1
      this.fetchDetailRecords()
    },

    // 重置明细筛选
    resetDetailFilter() {
      this.detailFilter.type = ''
      this.detailFilter.dateRange = null
      this.detailPagination.page = 1
      this.fetchDetailRecords()
    },

    // 按天分页
    handleDayPageChange(page) {
      this.dayPagination.page = page
      this.fetchDayRecords()
    },

    // 按月分页
    handleMonthPageChange(page) {
      this.monthPagination.page = page
      this.fetchMonthRecords()
    },







    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },

    // 积分工具方法：积分转元 (1元 = 1积分)
    pointsToYuan(points) {
      return new Decimal(points).div(1).toFixed(2)
    },

    // 积分工具方法：格式化积分显示
    formatPoints(points) {
      if (!points) return '0'
      return new Decimal(points).toNumber().toLocaleString()
    },

    // 计算积分变化
    calculatePointsChange(record) {
      const before = new Decimal(record.points_before || 0)
      const after = new Decimal(record.points_after || 0)
      return after.sub(before).toNumber()
    },

    // 使用 Decimal 格式化金额
    formatDecimal(value) {
      return new Decimal(value).toFixed(2)
    }
  }
}
</script>

<style scoped>
.account-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

.page-header {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
}

.page-header h2 {
  margin: 0;
  font-size: 26px;
  font-weight: 600;
  color: #303133;
  letter-spacing: 0.5px;
  position: relative;
  padding-left: 16px;
}

.page-header h2::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: linear-gradient(180deg, #409eff, #64b5f6);
  border-radius: 2px;
}

.page-header .el-button {
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 500;
  transition: all 0.3s;
}

.page-header .el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* 余额卡片 */
.balance-card {
  margin-bottom: 16px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: none;
}

.balance-card :deep(.el-card__body) {
  padding: 12px 16px;
}

.balance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0 8px;
  border-bottom: 1px solid rgba(235, 238, 245, 0.6);
}

.balance-info {
  position: relative;
}

.balance-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 2px;
  font-weight: 500;
}

.balance-amount {
  font-size: 24px;
  font-weight: 700;
  color: #f56c6c;
  line-height: 1.2;
}

.balance-sub {
  font-size: 11px;
  color: #c0c4cc;
  margin-top: 2px;
}

.balance-actions .el-button {
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  height: 36px;
  line-height: 20px;
}

.balance-stats {
  display: flex;
  padding-top: 8px;
}

.stat-item {
  flex: 1;
  text-align: center;
  border-right: 1px solid #ebeef5;
  padding: 4px 0;
}

.stat-item:last-child {
  border-right: none;
}

.stat-value {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 0;
}

.stat-label {
  font-size: 11px;
  color: #909399;
}

/* 记录卡片 */
.records-card {
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.records-card :deep(.el-card__body) {
  padding: 20px;
}

/* 标签页内容区域 */
.records-card :deep(.el-tabs__content) {
  overflow: visible;
}

/* 标签页样式优化 */
.records-card :deep(.el-tabs__header) {
  margin-bottom: 20px;
}

.records-card :deep(.el-tabs__nav-wrap::after) {
  height: 1px;
  background-color: #e4e7ed;
}

.records-card :deep(.el-tabs__item) {
  font-size: 15px;
  font-weight: 500;
  color: #606266;
  padding: 0 24px;
  height: 44px;
  line-height: 44px;
  transition: all 0.3s;
}

.records-card :deep(.el-tabs__item:hover) {
  color: #409eff;
}

.records-card :deep(.el-tabs__item.is-active) {
  color: #409eff;
  font-weight: 600;
}

.records-card :deep(.el-tabs__active-bar) {
  height: 3px;
  border-radius: 2px;
  background: linear-gradient(90deg, #409eff, #64b5f6);
}

/* 筛选表单 */
.filter-form {
  margin-bottom: 20px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-form .el-select,
.filter-form .el-date-picker {
  margin-right: 0 !important;
}

.filter-form .el-button {
  border-radius: 6px;
  padding: 9px 20px;
  font-weight: 500;
}

/* 表格样式优化 */
.records-card :deep(.el-table) {
  border: 1px solid #ebeef5;
}

.records-card :deep(.el-table__header-wrapper) {
  background: linear-gradient(135deg, #f5f7fa 0%, #eef1f6 100%);
}

.records-card :deep(.el-table th) {
  background: transparent;
  color: #303133;
  font-weight: 600;
  font-size: 14px;
  padding: 14px 0;
}

.records-card :deep(.el-table td) {
  padding: 14px 0;
  font-size: 14px;
  color: #606266;
}

.records-card :deep(.el-table--striped .el-table__body tr.el-table__row--striped td) {
  background: #fafbfc;
}

.records-card :deep(.el-table__row:hover td) {
  background-color: #f0f7ff !important;
  transition: background-color 0.3s;
}

/* 标签样式 */
.records-card :deep(.el-tag) {
  border-radius: 4px;
  padding: 0 10px;
  height: 26px;
  line-height: 24px;
  font-weight: 500;
}

.records-card :deep(.el-tag--success) {
  background-color: #f0f9eb;
  border-color: #e1f3d8;
  color: #67c23a;
}

.records-card :deep(.el-tag--danger) {
  background-color: #fef0f0;
  border-color: #fde2e2;
  color: #f56c6c;
}

/* 分页样式 */
.pagination-wrapper {
  text-align: right;
}

/* 金额样式 */
.amount-positive {
  color: #67c23a;
  font-weight: 600;
  font-size: 14px;
}

.amount-negative {
  color: #f56c6c;
  font-weight: 600;
  font-size: 14px;
}

/* 积分变化提示 */
.points-change-hint {
  color: #909399;
  font-size: 12px;
}

/* 按天/按月表格特殊样式 */
.records-card :deep(.el-table .cell) {
  line-height: 1.5;
}
</style>
